import React from "react";
import { Music2, Instagram, Linkedin, Phone } from "lucide-react";
import BackendPortal from "./admin/BackendPortal";

const LOGO = "/assets/logo.png";

const TIKTOK_URL = "https://www.tiktok.com/@authentic_momentum";
const INSTAGRAM_URL = "https://www.instagram.com/authenticmomentum1/";
const LINKEDIN_URL = "https://www.linkedin.com/in/cyrus-prescod-b96344292/";
const PHONE_DISPLAY = "(917) 722-8598";
const PHONE_HREF = "tel:+19177228598";

const Footer = () => (
  <footer
    data-testid="site-footer"
    className="relative border-t border-white/5 py-16 mt-10"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-10">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 pb-10 border-b border-gold/10 mb-12">
        <div className="flex items-center gap-4">
          <img src={LOGO} alt="Authentic Momentum" className="h-14 w-14 object-contain logo-mask" />
          <div>
            <div className="font-serif text-xl text-themed tracking-wider">Authentic Momentum</div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-gold/70 mt-1">
              Moving from Struggle to Flow
            </div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-themed-muted/70 mt-1">
              by Cyrus Prescod
            </div>
          </div>
        </div>
        <a
          href={PHONE_HREF}
          data-testid="footer-phone"
          aria-label={`Call Authentic Momentum at ${PHONE_DISPLAY}`}
          className="inline-flex items-center gap-3 group self-start md:self-auto"
        >
          <span className="w-9 h-9 rounded-full border border-gold/30 text-gold/85 group-hover:text-gold group-hover:border-gold/70 inline-flex items-center justify-center transition-colors">
            <Phone size={14} strokeWidth={1.4} />
          </span>
          <span className="flex flex-col leading-tight">
            <span className="text-[9px] tracking-[0.32em] uppercase text-gold/70 group-hover:text-gold/90 transition-colors">
              Direct Line
            </span>
            <span className="font-serif text-[15px] text-themed group-hover:text-gold transition-colors tracking-wider">
              {PHONE_DISPLAY}
            </span>
          </span>
        </a>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-10 items-start">
      {/* Column 1: The Path — the active offer ladder */}
      <div>
        <div className="text-[10px] tracking-[0.4em] uppercase text-gold/80 mb-4">The Path</div>
        <ul className="space-y-2 text-sm text-themed-muted">
          <li><a href="/start-here" className="hover:text-gold transition-colors" data-testid="footer-link-start-here">Start Here</a></li>
          <li><a href="/frequency-snapshot" className="hover:text-gold transition-colors" data-testid="footer-link-snapshot">Clarity Snapshot</a></li>
          <li><a href="/book" className="hover:text-gold transition-colors" data-testid="footer-link-book">Authentic Momentum</a></li>
          <li><a href="/start-here#step-blueprint" className="hover:text-gold transition-colors" data-testid="footer-link-blueprint">Personal Action Plan</a></li>
          <li><a href="/orientation-reset/schedule" className="hover:text-gold transition-colors" data-testid="footer-link-orientation-reset">Clarity Reset Session · $149</a></li>
        </ul>
      </div>

      {/* Column 2: The Work — proof, voices, journal, ecosystem */}
      <div>
        <div className="text-[10px] tracking-[0.4em] uppercase text-gold/80 mb-4">The Work</div>
        <ul className="space-y-2 text-sm text-themed-muted">
          <li><a href="/proof" className="hover:text-gold transition-colors" data-testid="footer-link-proof">Proof Gallery</a></li>
          <li><a href="/voices" className="hover:text-gold transition-colors" data-testid="footer-link-voices">Voices</a></li>
          <li><a href="/journal" className="hover:text-gold transition-colors" data-testid="footer-link-journal">Journal</a></li>
          <li><a href="/ecosystem" className="hover:text-gold transition-colors" data-testid="footer-link-ecosystem">Ecosystem</a></li>
        </ul>
      </div>

      {/* Column 3: The Foundation — about, social, legal */}
      <div>
        <div className="text-[10px] tracking-[0.4em] uppercase text-gold/80 mb-4">The Foundation</div>
        <ul className="space-y-2 text-sm text-themed-muted">
          <li><a href="/about" className="hover:text-gold transition-colors" data-testid="footer-link-about">About Authentic Momentum</a></li>
          <li><a href="/cyrus-prescod" className="hover:text-gold transition-colors" data-testid="footer-link-cyrus">Cyrus Prescod</a></li>
          <li><a href="/framework" className="hover:text-gold transition-colors" data-testid="footer-link-framework">The Framework</a></li>
          <li><a href="/press" className="hover:text-gold transition-colors" data-testid="footer-link-press">Press</a></li>
          <li><a href="/connect" className="hover:text-gold transition-colors" data-testid="footer-link-connect">Connect</a></li>
          <li><a href="/privacy" className="hover:text-gold transition-colors" data-testid="footer-link-privacy">Privacy</a></li>
          <li><a href="/terms" className="hover:text-gold transition-colors" data-testid="footer-link-terms">Terms</a></li>
        </ul>

        <div className="mt-6 flex items-center gap-3">
          <a
            href={TIKTOK_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Authentic Momentum on TikTok"
            data-testid="footer-social-tiktok"
            className="w-9 h-9 rounded-full border border-gold/30 text-gold/85 hover:text-gold hover:border-gold/70 inline-flex items-center justify-center transition-colors"
          >
            <Music2 size={15} strokeWidth={1.4} />
          </a>
          <a
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Authentic Momentum on Instagram"
            data-testid="footer-social-instagram"
            className="w-9 h-9 rounded-full border border-gold/30 text-gold/85 hover:text-gold hover:border-gold/70 inline-flex items-center justify-center transition-colors"
          >
            <Instagram size={15} strokeWidth={1.4} />
          </a>
          <a
            href={LINKEDIN_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Cyrus Prescod on LinkedIn"
            data-testid="footer-social-linkedin"
            className="w-9 h-9 rounded-full border border-gold/30 text-gold/85 hover:text-gold hover:border-gold/70 inline-flex items-center justify-center transition-colors"
          >
            <Linkedin size={15} strokeWidth={1.4} />
          </a>
        </div>
      </div>
      </div>

      <div className="mt-14 pt-8 border-t border-gold/10 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <p className="font-serif italic text-themed-muted leading-relaxed">
          "Energy is the currency. Alignment is the interest rate."
        </p>
        <div className="text-xs text-themed-muted/70 tracking-wide md:text-right">
          © {new Date().getFullYear()} Authentic Momentum. All rights reserved.
          <span className="mx-2 text-themed-muted/30">·</span>
          <span className="text-[10px] tracking-[0.3em] uppercase text-gold/60">
            Move from force to flow.
          </span>
          <BackendPortal />
        </div>
      </div>
    </div>
  </footer>
);

export default Footer;
